import { getMe, updateAccount } from '@/lib/api';
import { Car, Loader2, Save } from 'lucide-react';
import { useEffect, useState } from 'react';

type PricingModel = 'flat_fee' | 'per_mile';

interface TransportSettings {
  pricingModel: PricingModel;
  flatFeeMinor: string;
  perMileRateMinor: string;
  minimumFeeMinor: string;
  freeRadiusMiles: string;
  driverNotes: string;
}

const DEFAULT_SETTINGS: TransportSettings = {
  pricingModel: 'flat_fee',
  flatFeeMinor: '',
  perMileRateMinor: '',
  minimumFeeMinor: '',
  freeRadiusMiles: '',
  driverNotes: '',
};

interface RadioCardProps {
  id: string;
  checked: boolean;
  onChange: () => void;
  title: string;
  description: string;
}

function RadioCard({ id, checked, onChange, title, description }: RadioCardProps) {
  return (
    <label
      htmlFor={id}
      className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition-colors ${
        checked ? 'border-primary bg-primary/5' : 'border-border bg-surface hover:border-primary/40'
      }`}
    >
      <input
        type="radio"
        id={id}
        checked={checked}
        onChange={onChange}
        className="mt-0.5 size-4 accent-primary shrink-0"
      />
      <div>
        <p className={`text-sm font-medium ${checked ? 'text-primary' : 'text-foreground'}`}>
          {title}
        </p>
        <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
      </div>
    </label>
  );
}

export function TransportSettingsPage() {
  const [form, setForm] = useState<TransportSettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadAttempt, setLoadAttempt] = useState(0);

  useEffect(() => {
    setLoading(true);
    setLoadError(null);
    getMe()
      .then((me) => {
        const raw = me.activeAccount?.transportSettingsJson;
        if (raw) {
          try {
            const parsed = JSON.parse(raw) as Partial<TransportSettings>;
            setForm({ ...DEFAULT_SETTINGS, ...parsed });
          } catch {
            setForm(DEFAULT_SETTINGS);
          }
        }
      })
      // Surface load failures instead of silently falling back to defaults —
      // saving a defaults-populated form would overwrite the real settings.
      .catch(() => setLoadError('Could not load transport settings.'))
      .finally(() => setLoading(false));
  }, [loadAttempt]);

  function set(key: keyof TransportSettings, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
    setSaved(false);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await updateAccount({ transportSettingsJson: form as unknown as Record<string, unknown> });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError((e as Error).message ?? 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-8">
        <Loader2 className="size-4 animate-spin" strokeWidth={1.75} />
        Loading…
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="space-y-3 py-8">
        <div className="rounded-lg bg-destructive/10 text-destructive px-4 py-3 text-sm">
          {loadError}
        </div>
        <button
          type="button"
          onClick={() => setLoadAttempt((n) => n + 1)}
          className="rounded-md border border-border px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground press"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-medium tracking-tight">Transport</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Configure pricing defaults and driver instructions for pickup and dropoff bookings.
        </p>
      </div>

      <form onSubmit={(e) => void handleSubmit(e)} className="space-y-8">
        {error && (
          <div className="rounded-lg bg-destructive/10 text-destructive px-4 py-3 text-sm">
            {error}
          </div>
        )}

        {/* Pricing model */}
        <section className="space-y-4">
          <div>
            <h2 className="text-sm font-semibold tracking-tight">Pricing model</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Choose how transport fees are calculated.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <RadioCard
              id="pricing-flat"
              checked={form.pricingModel === 'flat_fee'}
              onChange={() => set('pricingModel', 'flat_fee')}
              title="Flat fee per trip"
              description="A fixed charge applied to each pickup or dropoff leg."
            />
            <RadioCard
              id="pricing-mile"
              checked={form.pricingModel === 'per_mile'}
              onChange={() => set('pricingModel', 'per_mile')}
              title="Per mile rate"
              description="Charge calculated from the distance driven for each leg."
            />
          </div>
        </section>

        {/* Flat fee inputs */}
        {form.pricingModel === 'flat_fee' && (
          <section className="space-y-4 rounded-xl border border-border bg-surface p-5">
            <h3 className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
              Flat fee settings
            </h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <label
                  htmlFor="flat-fee"
                  className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
                >
                  Flat fee
                </label>
                <div className="flex items-center gap-2">
                  <input
                    id="flat-fee"
                    type="number"
                    min={0}
                    value={form.flatFeeMinor}
                    onChange={(e) => set('flatFeeMinor', e.target.value)}
                    placeholder="0"
                    className="num h-9 w-36 rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring/30"
                  />
                  <span className="text-xs text-muted-foreground">pence / cents</span>
                </div>
              </div>
              <div className="space-y-1.5">
                <label
                  htmlFor="min-fee"
                  className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
                >
                  Minimum fee (optional)
                </label>
                <div className="flex items-center gap-2">
                  <input
                    id="min-fee"
                    type="number"
                    min={0}
                    value={form.minimumFeeMinor}
                    onChange={(e) => set('minimumFeeMinor', e.target.value)}
                    placeholder="—"
                    className="num h-9 w-36 rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring/30"
                  />
                  <span className="text-xs text-muted-foreground">pence / cents</span>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Per-mile rate inputs */}
        {form.pricingModel === 'per_mile' && (
          <section className="space-y-4 rounded-xl border border-border bg-surface p-5">
            <h3 className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
              Per mile settings
            </h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <label
                  htmlFor="per-mile"
                  className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
                >
                  Rate per mile
                </label>
                <div className="flex items-center gap-2">
                  <input
                    id="per-mile"
                    type="number"
                    min={0}
                    value={form.perMileRateMinor}
                    onChange={(e) => set('perMileRateMinor', e.target.value)}
                    placeholder="0"
                    className="num h-9 w-36 rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring/30"
                  />
                  <span className="text-xs text-muted-foreground">pence / cents per mile</span>
                </div>
              </div>
              <div className="space-y-1.5">
                <label
                  htmlFor="min-fee-mile"
                  className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
                >
                  Minimum fee (optional)
                </label>
                <div className="flex items-center gap-2">
                  <input
                    id="min-fee-mile"
                    type="number"
                    min={0}
                    value={form.minimumFeeMinor}
                    onChange={(e) => set('minimumFeeMinor', e.target.value)}
                    placeholder="—"
                    className="num h-9 w-36 rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring/30"
                  />
                  <span className="text-xs text-muted-foreground">pence / cents</span>
                </div>
              </div>
              <div className="space-y-1.5">
                <label
                  htmlFor="free-radius"
                  className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
                >
                  Free radius (optional)
                </label>
                <div className="flex items-center gap-2">
                  <input
                    id="free-radius"
                    type="number"
                    min={0}
                    value={form.freeRadiusMiles}
                    onChange={(e) => set('freeRadiusMiles', e.target.value)}
                    placeholder="—"
                    className="num h-9 w-36 rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring/30"
                  />
                  <span className="text-xs text-muted-foreground">miles</span>
                </div>
                <p className="text-xs text-muted-foreground">Trips within this radius are free.</p>
              </div>
            </div>
          </section>
        )}

        {/* Free radius for flat fee */}
        {form.pricingModel === 'flat_fee' && (
          <section className="space-y-4 rounded-xl border border-border bg-surface p-5">
            <h3 className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
              Distance
            </h3>
            <div className="space-y-1.5">
              <label
                htmlFor="free-radius-flat"
                className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
              >
                Free radius (optional)
              </label>
              <div className="flex items-center gap-2">
                <input
                  id="free-radius-flat"
                  type="number"
                  min={0}
                  value={form.freeRadiusMiles}
                  onChange={(e) => set('freeRadiusMiles', e.target.value)}
                  placeholder="—"
                  className="num h-9 w-36 rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring/30"
                />
                <span className="text-xs text-muted-foreground">miles</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Trips within this radius are offered free of charge.
              </p>
            </div>
          </section>
        )}

        {/* Driver notes */}
        <section className="space-y-4 rounded-xl border border-border bg-surface p-5">
          <div>
            <h3 className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
              Default driver notes
            </h3>
            <p className="mt-1 text-xs text-muted-foreground">
              Pre-fills the notes field when creating a transport assignment in the booking wizard.
              Drivers can update per-booking.
            </p>
          </div>
          <textarea
            value={form.driverNotes}
            onChange={(e) => set('driverNotes', e.target.value)}
            rows={3}
            placeholder="e.g. Always use rear seat belt attachment. Confirm address before departing."
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none resize-none focus:ring-2 focus:ring-ring/30"
          />
        </section>

        {/* Save */}
        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground press disabled:opacity-60"
          >
            {saving ? (
              <Loader2 className="size-4 animate-spin" strokeWidth={1.75} />
            ) : (
              <Save className="size-4" strokeWidth={1.75} />
            )}
            {saving ? 'Saving…' : 'Save settings'}
          </button>
          {saved && (
            <span className="flex items-center gap-1.5 text-sm text-success">
              <Car className="size-4" strokeWidth={1.75} />
              Settings saved
            </span>
          )}
        </div>
      </form>
    </div>
  );
}
